import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  IconButton,
  Avatar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import HailIcon from "@mui/icons-material/Hail";
import InfoIcon from "@mui/icons-material/Info";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import HistoryIcon from "@mui/icons-material/History";
import WorkIcon from '@mui/icons-material/Work';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import {
  fetchEmployeeList,
  fetchEmployeeDetails,
  fetchEmployeeOtherInfo,
  fetchEmployeeNoPay,
  fetchEmployeePreYearSummary,
} from "../../action/EmpDetailsAction";
import EmpDetailsService from "../../service/EmpDetailsService";

const EXCLUDED_LOCATIONS = ["CAC", "HAO", "IIM", "ISM", "WEL", "MPM"];

const CATEGORY_LABELS = {
  D: "Divisional Head",
  E: "Executive",
  S: "Supervisor",
  C: "Clerical",
  I: "Industrial",
  T: "Trainee",
};

const CATEGORY_OPTIONS = [
  { value: "E", label: "Executive" },
  { value: "S", label: "Supervisor" },
  { value: "C", label: "Clerical" },
  { value: "I", label: "Industrial" },
  { value: "T", label: "Trainee" },
];

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const generateYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - i);
};

const filterSelectSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  color: "#1a2b45",
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d6dce5" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#330066" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#330066" },
};

const filterLabelSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "12px",
  color: "#8a9bb0",
  "&.Mui-focused": { color: "#330066" },
};

const filterMenuItemSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "12px",
};

const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "6px 12px",
  fontFamily: "'Poppins', sans-serif",
}));

const SearchContainer = styled(Box)(() => ({
  position: "relative",
  marginBottom: "8px",
}));

const DropdownList = styled(Paper)(() => ({
  position: "absolute",
  top: "calc(100% + 3px)",
  left: 0,
  right: 0,
  zIndex: 200,
  maxHeight: "240px",
  overflowY: "auto",
  borderRadius: "10px",
  boxShadow: "0px 6px 20px rgba(0,0,0,0.14)",
  border: "1px solid #d6dce5",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "#f1f4f8" },
  "&::-webkit-scrollbar-thumb": { background: "#330066", borderRadius: "3px" },
}));

const DropdownItem = styled(Box)(({ highlighted }) => ({
  padding: "7px 14px",
  cursor: "pointer",
  fontFamily: "'Poppins', sans-serif",
  fontSize: "13px",
  color: "#1a2b45",
  backgroundColor: highlighted ? "rgba(21, 76, 121, 0.08)" : "transparent",
  borderBottom: "1px solid #f0f4f8",
  transition: "background 0.15s ease",
  "&:hover": { backgroundColor: "rgba(21, 76, 121, 0.08)" },
  "&:last-of-type": { borderBottom: "none" },
}));

const ProfileCard = styled(Paper)(() => ({
  borderRadius: "14px",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.10)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  overflow: "hidden",
}));

const ProfileHeader = styled(Box)(() => ({
  background: "linear-gradient(135deg, #0e3c66 0%, #330066 60%, #1a5e96 100%)",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: "14px",
}));

const DetailRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "5px 14px",
  borderBottom: "1px solid #f0f4f8",
  fontFamily: "'Poppins', sans-serif",
  "&:last-of-type": { borderBottom: "none" },
}));

const DetailLabel = styled(Typography)(() => ({
  fontSize: "11px",
  fontWeight: 600,
  color: "#8a9bb0",
  fontFamily: "'Poppins', sans-serif",
  minWidth: "100px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
}));

const DetailValue = styled(Typography)(() => ({
  fontSize: "13px",
  fontWeight: 500,
  color: "#1a2b45",
  fontFamily: "'Poppins', sans-serif",
  flex: 1,
}));

const IconBox = styled(Box)(({ color }) => ({
  width: "24px",
  height: "24px",
  borderRadius: "6px",
  backgroundColor: color || "rgba(21, 76, 121, 0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const EmptyState = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "36px 20px",
  borderRadius: "14px",
  border: "2px dashed #d6dce5",
  backgroundColor: "#f9fafc",
}));

const StyledTabs = styled(Tabs)(() => ({
  borderBottom: "1px solid #f0f4f8",
  minHeight: "36px",
  "& .MuiTabs-indicator": { backgroundColor: "#330066", height: "2px", borderRadius: "2px" },
  "& .MuiTab-root": {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "11px",
    fontWeight: 600,
    color: "#8a9bb0",
    textTransform: "none",
    minHeight: "36px",
    padding: "0 12px",
    "&.Mui-selected": { color: "#330066" },
  },
}));

const StyledTableHead = styled(TableHead)(() => ({
  "& .MuiTableCell-root": {
    backgroundColor: "#f1f5f9",
    fontFamily: "'Poppins', sans-serif",
    fontSize: "10px",
    fontWeight: 700,
    color: "#8a9bb0",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: "2px solid #e2e8f0",
    padding: "6px 12px",
    whiteSpace: "nowrap",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:hover": { backgroundColor: "rgba(21,76,121,0.03)" },
  "&:nth-of-type(even)": { backgroundColor: "#fafbfd" },
  "& .MuiTableCell-root": {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "12px",
    color: "#1a2b45",
    borderBottom: "1px solid #f0f4f8",
    padding: "6px 12px",
  },
}));

const ResultsCard = styled(Paper)(() => ({
  borderRadius: "14px",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.10)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  marginBottom: "8px",
}));

const ResultsHeader = styled(Box)(() => ({
  background: "linear-gradient(135deg, #0e3c66 0%, #330066 60%, #1a5e96 100%)",
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const ResultItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "9px 14px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f4f8",
  transition: "background 0.15s ease",
  "&:hover": { backgroundColor: "rgba(21, 76, 121, 0.06)" },
  "&:last-of-type": { borderBottom: "none" },
}));

export default function Employees() {
  const dispatch = useDispatch();

  const {
    employeeList = [],
    employeeListLoading,
    employeeListError,
    employeeDetails = {},
    employeeDetailsLoading,
    employeeDetailsError,
    employeeOtherInfo = [],
    employeeOtherInfoLoading,
    employeeNoPay = [],
    employeeNoPayLoading,
    employeePreYearSummary = [],
    employeePreYearSummaryLoading,
  } = useSelector((state) => state.empDetails || {});

  const [selectedServiceNo, setSelectedServiceNo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState(0);

  // status defaults to Active
  const [filterLocation, setFilterLocation] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("A");

  // Search results state
  const [searchResults, setSearchResults] = useState(null); // null = not searched yet

  // No Pay year defaults 2025
  const [noPayYear, setNoPayYear] = useState(2025);
  const yearOptions = generateYearOptions();

  useEffect(() => {
    dispatch(fetchEmployeeList());
  }, [dispatch]);

  useEffect(() => {
    if (selectedServiceNo) {
      dispatch(fetchEmployeeDetails(selectedServiceNo));
      dispatch(fetchEmployeeOtherInfo(selectedServiceNo));
      dispatch(fetchEmployeePreYearSummary(selectedServiceNo));
      setNoPayYear(2025);
    }
  }, [selectedServiceNo, dispatch]);

  useEffect(() => {
    if (selectedServiceNo) {
      const yr = noPayYear.toString();
      dispatch(
        fetchEmployeeNoPay(
          selectedServiceNo,
          yr,
          `${yr}-01`,
          `${yr}-12`,
          yr
        )
      );
    }
  }, [selectedServiceNo, noPayYear, dispatch]);

  // Location options
  const locationOptions = useMemo(() => {
    const locs = [
      ...new Set(
        employeeList
          .map((e) => e.EmployeeLocation)
          .filter((loc) => Boolean(loc) && !EXCLUDED_LOCATIONS.includes(loc))
      ),
    ];
    return locs.sort();
  }, [employeeList]);

  // inline search dropdown
  const filteredList = useMemo(() => {
    return employeeList.filter((emp) => {
      const q = searchQuery.toLowerCase();
      const matchQ =
        !searchQuery.trim() ||
        emp.ServiceNo?.toLowerCase().includes(q) ||
        emp.EmployeeName?.toLowerCase().includes(q);
      const matchLoc = !filterLocation || emp.EmployeeLocation === filterLocation;
      const matchCat = !filterCategory || emp.EmployeeCategory === filterCategory;
      const matchStat = !filterStatus || emp.EmployeeStatus === filterStatus;
      return matchQ && matchLoc && matchCat && matchStat;
    });
  }, [searchQuery, employeeList, filterLocation, filterCategory, filterStatus]);

  // Other info  date order desc
  const sortedOtherInfo = useMemo(() => {
    if (!employeeOtherInfo || employeeOtherInfo.length === 0) return [];
    return [...employeeOtherInfo].sort((a, b) => new Date(b.Date) - new Date(a.Date));
  }, [employeeOtherInfo]);

  // Handle Search Button 
  const handleSearchButtonClick = () => {
    const results = employeeList.filter((emp) => {
      const q = searchQuery.toLowerCase();
      const matchQ =
        !searchQuery.trim() ||
        emp.ServiceNo?.toLowerCase().includes(q) ||
        emp.EmployeeName?.toLowerCase().includes(q);
      const matchLoc = !filterLocation || emp.EmployeeLocation === filterLocation;
      const matchCat = !filterCategory || emp.EmployeeCategory === filterCategory;
      const matchStat = !filterStatus || emp.EmployeeStatus === filterStatus;
      return matchQ && matchLoc && matchCat && matchStat;
    });
    setSearchResults(results);
    setSelectedServiceNo(""); // clear any selected employee
    setDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setDropdownOpen(true);
    setHighlightedIndex(-1);
    setSearchResults(null);
    setSelectedServiceNo("");
  };

  const handleClear = () => {
    setSearchQuery("");
    setSelectedServiceNo("");
    setDropdownOpen(false);
    setHighlightedIndex(-1);
    setSearchResults(null);
  };

  // Select from inline dropdown
  const handleSelect = (emp) => {
    setSelectedServiceNo(emp.ServiceNo);
    setSearchQuery(`${emp.EmployeeName} (${emp.ServiceNo})`);
    setDropdownOpen(false);
    setActiveTab(0);
    setSearchResults(null);
  };

  // Select from results list
  const handleSelectFromResults = (emp) => {
    setSelectedServiceNo(emp.ServiceNo);
    setSearchQuery(`${emp.EmployeeName} (${emp.ServiceNo})`);
    setActiveTab(0);
    setSearchResults(null); 
  };

  // Back to results list from profile
  const handleBackToResults = () => {
    setSelectedServiceNo("");
    setSearchQuery("");
    setSearchResults(null);
  };

  const handleKeyDown = (e) => {
    if (!dropdownOpen || filteredList.length === 0) return;
    if (e.key === "ArrowDown") setHighlightedIndex((prev) => Math.min(prev + 1, filteredList.length - 1));
    else if (e.key === "ArrowUp") setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    else if (e.key === "Enter" && highlightedIndex >= 0) handleSelect(filteredList[highlightedIndex]);
    else if (e.key === "Enter" && highlightedIndex < 0) handleSearchButtonClick();
    else if (e.key === "Escape") setDropdownOpen(false);
  };

  const hasDetails = selectedServiceNo && Object.keys(employeeDetails).length > 0;
  const showResults = searchResults !== null && !selectedServiceNo;
  const showEmpty = !selectedServiceNo && searchResults === null && !employeeDetailsLoading;

  const detailFields = [
    { icon: <WorkIcon sx={{ fontSize: 15, color: "#0e89db" }} />, label: "W.Category", value: CATEGORY_LABELS[employeeDetails.WorkCategory] || employeeDetails.WorkCategory, color: "rgba(230,81,0,0.08)" },
    { icon: <EmailIcon sx={{ fontSize: 15, color: "#e65100" }} />, label: "Email", value: employeeDetails.Email, color: "rgba(230,81,0,0.08)" },
    { icon: <PhoneIcon sx={{ fontSize: 15, color: "#6a1b9a" }} />, label: "Mobile", value: employeeDetails.MobileNo, color: "rgba(106,27,154,0.08)", isPhone: true },
    { icon: <LocationOnIcon sx={{ fontSize: 15, color: "#c62828" }} />, label: "Location", value: employeeDetails.Location, color: "rgba(198,40,40,0.08)" },
    { icon: <BusinessIcon sx={{ fontSize: 15, color: "#00838f" }} />, label: "Con Start", value: employeeDetails.ConStartDate, color: "rgba(0,131,143,0.08)" },
    { icon: <HailIcon sx={{ fontSize: 15, color: "#330066" }} />, label: "Con End", value: employeeDetails.ConEndDate, color: "rgba(21,76,121,0.08)" },
  ];

  const tabLoadingText = (loading, emptyMsg) =>
    loading ? (
      <Typography sx={{ color: "#8a9bb0", fontSize: "12px", fontFamily: "'Poppins', sans-serif", textAlign: "center", py: 2 }}>
        Loading...
      </Typography>
    ) : (
      <Typography sx={{ color: "#8a9bb0", fontSize: "12px", fontFamily: "'Poppins', sans-serif", textAlign: "center", py: 2 }}>
        {emptyMsg}
      </Typography>
    );

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      {/* <HeaderBannerPage /> */}

      <PageContainer>
        {/* <Typography
          sx={{
            fontSize: "17px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 1,
            mt: 0.5,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Employee Details
        </Typography> */}

        {/* Filter Dropdowns (above search bar) */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", mb: 1 }}>
          {/* Location */}
          <FormControl size="small">
            <InputLabel sx={filterLabelSx}>Location</InputLabel>
            <Select
              value={filterLocation}
              label="Location"
              onChange={(e) => {
                setFilterLocation(e.target.value);
                setSelectedServiceNo("");
                setSearchQuery("");
                setSearchResults(null);
              }}
              sx={filterSelectSx}
            >
              <MenuItem value="" sx={filterMenuItemSx}>All locations</MenuItem>
              {locationOptions.map((loc) => (
                <MenuItem key={loc} value={loc} sx={filterMenuItemSx}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category */}
          <FormControl size="small">
            <InputLabel sx={filterLabelSx}>Category</InputLabel>
            <Select
              value={filterCategory}
              label="Category"
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setSelectedServiceNo("");
                setSearchQuery("");
                setSearchResults(null);
              }}
              sx={filterSelectSx}
            >
              <MenuItem value="" sx={filterMenuItemSx}>All categories</MenuItem>
              {CATEGORY_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value} sx={filterMenuItemSx}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl size="small">
            <InputLabel sx={filterLabelSx}>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setSelectedServiceNo("");
                setSearchQuery("");
                setSearchResults(null);
              }}
              sx={filterSelectSx}
            >
              <MenuItem value="A" sx={filterMenuItemSx}>Active</MenuItem>
              <MenuItem value="I" sx={filterMenuItemSx}>Inactive</MenuItem>
              <MenuItem value="" sx={filterMenuItemSx}>All statuses</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Search Box + Button */}
        <SearchContainer onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Box sx={{ flex: 1, position: "relative" }}>
              <TextField
                fullWidth
                placeholder="Search by name or service no..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => { if (searchQuery) setDropdownOpen(true); }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#330066", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{ color: "#8a9bb0", "&:hover": { color: "#330066" } }}
                      >
                        <ClearIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: {
                    borderRadius: "20px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    backgroundColor: "#f9fafc",
                    "& fieldset": { borderColor: "#d6dce5" },
                    "&:hover fieldset": { borderColor: "#330066" },
                    "&.Mui-focused fieldset": { borderColor: "#330066" },
                  },
                }}
                sx={{ "& .MuiInputBase-input": { fontFamily: "'Poppins', sans-serif", fontSize: "14px" } }}
              />

              {/* Inline dropdown  */}
              {dropdownOpen && searchQuery && searchResults === null && (
                <DropdownList>
                  {employeeListLoading ? (
                    <DropdownItem>
                      <Typography sx={{ color: "#8a9bb0", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                        Loading...
                      </Typography>
                    </DropdownItem>
                  ) : filteredList.length === 0 ? (
                    <DropdownItem>
                      <Typography sx={{ color: "#8a9bb0", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                        No employees found
                      </Typography>
                    </DropdownItem>
                  ) : (
                    filteredList.map((emp, index) => (
                      <DropdownItem
                        key={emp.ServiceNo}
                        highlighted={index === highlightedIndex ? 1 : 0}
                        onMouseDown={() => handleSelect(emp)}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "13px", fontFamily: "'Poppins', sans-serif", color: "#1a2b45" }}
                          >
                            {emp.EmployeeName}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            {emp.EmployeeCategory && (
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  fontFamily: "'Poppins', sans-serif",
                                  color: "#330066",
                                  fontWeight: 600,
                                  backgroundColor: "rgba(25,118,210,0.08)",
                                  borderRadius: "20px",
                                  px: 0.8,
                                  py: 0.2,
                                }}
                              >
                                {CATEGORY_LABELS[emp.EmployeeCategory] || emp.EmployeeCategory}
                              </Typography>
                            )}
                            <Typography
                              sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif", color: "#8a9bb0", fontWeight: 600 }}
                            >
                              {emp.ServiceNo}
                            </Typography>
                          </Box>
                        </Box>
                        {emp.EmployeeLocation && (
                          <Typography
                            sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif", color: "#8a9bb0", mt: 0.3 }}
                          >
                            {emp.EmployeeLocation}
                          </Typography>
                        )}
                      </DropdownItem>
                    ))
                  )}
                </DropdownList>
              )}
            </Box>

            {/* Search Button */}
            <Button
              variant="contained"
              onClick={handleSearchButtonClick}
              disabled={employeeListLoading}
              sx={{
                borderRadius: "20px",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: "#330066",
                color: "#ffffff",
                padding: "7px 18px",
                height: "40px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "0px 2px 8px rgba(25,118,210,0.25)",
                "&:hover": {
                  backgroundColor: "#0e3c66",
                  boxShadow: "0px 4px 12px rgba(25,118,210,0.35)",
                },
                "&:disabled": {
                  backgroundColor: "#b0bec5",
                  color: "#ffffff",
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Search
            </Button>
          </Box>
        </SearchContainer>

        {/* Errors */}
        {employeeListError && (
          <Typography sx={{ color: "red", fontSize: "13px", fontFamily: "'Poppins', sans-serif", mb: 1 }}>
            {employeeListError}
          </Typography>
        )}
        {employeeDetailsError && (
          <Typography sx={{ color: "red", fontSize: "13px", fontFamily: "'Poppins', sans-serif", mb: 1 }}>
            {employeeDetailsError}
          </Typography>
        )}

        {employeeDetailsLoading && (
          <Typography sx={{ color: "#8a9bb0", fontSize: "13px", fontFamily: "'Poppins', sans-serif", textAlign: "center", py: 3 }}>
            Loading employee details...
          </Typography>
        )}

        {/* Empty State (initial) */}
        {showEmpty && !employeeDetailsLoading && (
          <EmptyState>
            <SearchIcon sx={{ fontSize: "48px", color: "#d0dae8", mb: 1.5 }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#8a9bb0", fontFamily: "'Poppins', sans-serif" }}>
              Search for an employee
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#b0bec5", fontFamily: "'Poppins', sans-serif", mt: 0.5 }}>
              Use the filters and search button to find employees
            </Typography>
          </EmptyState>
        )}

        {/* Search Results List */}
        {showResults && !employeeDetailsLoading && (
          <ResultsCard>
            <ResultsHeader>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#ffffff", fontFamily: "'Poppins', sans-serif" }}>
                Search Results
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontFamily: "'Poppins', sans-serif",
                  color: "rgba(255,255,255,0.75)",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  borderRadius: "20px",
                  px: 1.2,
                  py: 0.2,
                  fontWeight: 600,
                }}
              >
                {searchResults.length} found
              </Typography>
            </ResultsHeader>

            {searchResults.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography sx={{ color: "#8a9bb0", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                  No employees match the selected filters
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: "420px", overflowY: "auto", "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-track": { background: "#f1f4f8" }, "&::-webkit-scrollbar-thumb": { background: "#330066", borderRadius: "3px" } }}>
                {searchResults.map((emp) => (
                  <ResultItem key={emp.ServiceNo} onClick={() => handleSelectFromResults(emp)}>
                    <Avatar
                      src={EmpDetailsService.GetEmployeeImage(emp.ServiceNo)}
                      alt={emp.EmployeeName}
                      sx={{ width: 36, height: 36, flexShrink: 0, border: "1px solid #d6dce5" }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#330066",
                            fontFamily: "'Poppins', sans-serif",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                        {emp.EmployeeName}
                      </Typography>
                      {emp.EmployeeLocation && (
                        <Typography sx={{ fontSize: "11px", color: "#8a9bb0", fontFamily: "'Poppins', sans-serif" }}>
                          {emp.EmployeeLocation}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        flexDirection: "column",
                        gap: 0.5,
                        flexShrink: 0,
                        maxWidth: "45%",
                      }}
                    >
                      {emp.EmployeeDesignation && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontFamily: "'Poppins', sans-serif",
                            color: "#330066",
                            fontWeight: 600,
                            backgroundColor: "rgba(25,118,210,0.08)",
                            borderRadius: "20px",
                            px: 0.8,
                            py: 0.2,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {emp.EmployeeDesignation}
                        </Typography>
                      )}

                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#8a9bb0",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {emp.ServiceNo}
                      </Typography>
                    </Box>
                  </ResultItem>
                ))}
              </Box>
            )}
          </ResultsCard>
        )}

        {/* Employee Profile Card */}
        {hasDetails && !employeeDetailsLoading && (
          <ProfileCard>
            {/* Header */}
            <ProfileHeader>
              <Avatar
                src={EmpDetailsService.GetEmployeeImage(selectedServiceNo)}
                alt={employeeDetails.ReportName}
                sx={{
                  width: 60,
                  height: 60,
                  border: "2px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: "15px", fontWeight: 700, color: "#ffffff", fontFamily: "'Poppins', sans-serif", lineHeight: 1.2, mb: 0.3 }}
                >
                  {employeeDetails.ReportName}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "rgba(255,255,255,0.75)", fontFamily: "'Poppins', sans-serif", fontWeight: 500, mb: 0.3 }}
                >
                  {employeeDetails.Designation}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                  <Box sx={{ display: "inline-block", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "20px", px: 1.2, py: 0.2 }}>
                    <Typography sx={{ fontSize: "11px", color: "#ffffff", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                      {employeeDetails.ServiceNo}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "inline-block", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "20px", px: 1.2, py: 0.2 }}>
                    <Typography sx={{ fontSize: "11px", color: "#ffffff", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                      {employeeDetails.CdlBarcodeNo}
                    </Typography>
                  </Box>
                  {employeeDetails.EmployeeCategory && (
                    <Box sx={{ display: "inline-block", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "20px", px: 1.2, py: 0.2 }}>
                      <Typography sx={{ fontSize: "11px", color: "#ffffff", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                        {CATEGORY_LABELS[employeeDetails.EmployeeCategory] || employeeDetails.EmployeeCategory}
                      </Typography>
                    </Box>
                  )}
                  {employeeDetails.EmployeeStatus && (
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: employeeDetails.EmployeeStatus === "A" ? "rgba(46,125,50,0.3)" : "rgba(198,40,40,0.3)",
                        border: `1px solid ${employeeDetails.EmployeeStatus === "A" ? "rgba(46,125,50,0.5)" : "rgba(198,40,40,0.5)"}`,
                        borderRadius: "20px",
                        px: 1.2,
                        py: 0.2,
                      }}
                    >
                      <Typography sx={{ fontSize: "11px", color: "#ffffff", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                        {employeeDetails.EmployeeStatus === "A" ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  )}
                  {/* Back button — only shown when navigated from search results */}
                  <Box
                    onClick={handleBackToResults}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      backgroundColor: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: "20px",
                      px: 1.2,
                      py: 0.2,
                      cursor: "pointer",
                      ml: "auto",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 12, color: "#ffffff" }} />
                    <Typography sx={{ fontSize: "11px", color: "#ffffff", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                      Back
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ProfileHeader>

            {/* Tabs */}
            <StyledTabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab icon={<PersonIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Profile" />
              <Tab icon={<InfoIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Other Info" />
              <Tab icon={<MoneyOffIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="No Pay" />
              <Tab icon={<HistoryIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Attendance Summary" />
            </StyledTabs>

            {/* Tab: Profile */}
            {activeTab === 0 && (
              <Box>
                {detailFields.map((field, index) =>
                  field.value ? (
                    <DetailRow key={index}>
                      <IconBox color={field.color}>{field.icon}</IconBox>
                      <DetailLabel>{field.label}</DetailLabel>
                      <DetailValue>{field.value}</DetailValue>
                      {field.isPhone && (
                        <Box
                          component="a"
                          href={`tel:${field.value}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            borderRadius: "6px",
                            backgroundColor: "rgba(46,125,50,0.1)",
                            color: "#2e7d32",
                            flexShrink: 0,
                            textDecoration: "none",
                            transition: "background 0.15s ease",
                            "&:hover": { backgroundColor: "rgba(46,125,50,0.2)" },
                          }}
                        >
                          <CallIcon sx={{ fontSize: 15 }} />
                        </Box>
                      )}
                    </DetailRow>
                  ) : null
                )}
              </Box>
            )}

            {/* Tab: Other Info */}
            {activeTab === 1 && (
              <Box sx={{ p: 1 }}>
                {employeeOtherInfoLoading || sortedOtherInfo.length === 0 ? (
                  tabLoadingText(employeeOtherInfoLoading, "No other info available")
                ) : (
                  <TableContainer sx={{ borderRadius: "10px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    <Table size="small">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell sx={{ width: "1%", whiteSpace: "nowrap", pr: 1 }}>#</TableCell>
                          <TableCell sx={{ width: "1%", whiteSpace: "nowrap", pr: 2 }}>Date</TableCell>
                          <TableCell sx={{ width: "auto" }}>Details</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {sortedOtherInfo.map((row, i) => (
                          <StyledTableRow key={i}>
                            <TableCell sx={{ color: "#8a9bb0", fontWeight: 600, fontSize: "12px", width: "1%", whiteSpace: "nowrap", pr: 1 }}>
                              {sortedOtherInfo.length - i}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#330066", width: "1%", whiteSpace: "nowrap", pr: 2 }}>
                              {row.Date}
                            </TableCell>
                            <TableCell sx={{ width: "auto" }}>{row.Info}</TableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Tab: No Pay */}
            {activeTab === 2 && (
              <Box sx={{ p: 1 }}>
                <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={filterLabelSx}>Select Year</InputLabel>
                    <Select
                      value={noPayYear}
                      label="Select Year"
                      onChange={(e) => setNoPayYear(e.target.value)}
                      sx={filterSelectSx}
                    >
                      {yearOptions.map((yr) => (
                        <MenuItem key={yr} value={yr} sx={filterMenuItemSx}>{yr}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {employeeNoPayLoading && (
                    <Typography sx={{ fontSize: "12px", color: "#8a9bb0", fontFamily: "'Poppins', sans-serif" }}>
                      Loading...
                    </Typography>
                  )}
                </Box>

                {!employeeNoPayLoading && employeeNoPay.length === 0 ? (
                  tabLoadingText(false, `No pay records for ${noPayYear}`)
                ) : (
                  !employeeNoPayLoading && (
                    <TableContainer sx={{ borderRadius: "10px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                      <Table size="small">
                        <StyledTableHead>
                          <TableRow>
                            <TableCell sx={{ width: "50px" }}>#</TableCell>
                            <TableCell>Month</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </StyledTableHead>
                        <TableBody>
                          {employeeNoPay.map((row, i) => (
                            <StyledTableRow key={i}>
                              <TableCell sx={{ color: "#8a9bb0", fontWeight: 600, fontSize: "12px" }}>{i + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {MONTH_NAMES[parseInt(row.VMonth)] || row.VMonth}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: "#c62828" }}>
                                {Number(row.HesAmount).toLocaleString("en-LK", { minimumFractionDigits: 2 })}
                              </TableCell>
                            </StyledTableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                            <TableCell
                              colSpan={2}
                              sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "10px",
                                fontWeight: 700,
                                color: "#8a9bb0",
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                borderTop: "2px solid #e2e8f0",
                                padding: "6px 12px",
                              }}
                            >
                              Total
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#c62828",
                                borderTop: "2px solid #e2e8f0",
                                padding: "6px 12px",
                              }}
                            >
                              {employeeNoPay
                                .reduce((sum, row) => sum + (parseFloat(row.HesAmount) || 0), 0)
                                .toLocaleString("en-LK", { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                )}
              </Box>
            )}

            {/* Tab: Attendance Summary */}
            {activeTab === 3 && (
              <Box sx={{ p: 1 }}>
                {employeePreYearSummaryLoading || employeePreYearSummary.length === 0 ? (
                  tabLoadingText(employeePreYearSummaryLoading, "No previous year summary available")
                ) : (
                  <TableContainer sx={{ borderRadius: "10px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    <Table size="small">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell sx={{ width: "50px" }}>#</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {employeePreYearSummary.map((row, i) => (
                          <StyledTableRow key={i}>
                            <TableCell sx={{ color: "#8a9bb0", fontWeight: 600, fontSize: "12px" }}>{i + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: "#330066" }}>{row.Year}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {row.PreYearSummary}%
                            </TableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </ProfileCard>
        )}
      </PageContainer>
    </Box>
  );
}
